import { action, computed, IObservableArray, observable } from 'mobx';

import { ILocalization } from 'bernie-l10n';

export interface RoomConfig {
  adults: number;
  children: number[];
}

export interface Travelers {
  adults?: string;
  children?: string;
}

export interface TravelerCount {
  guests: number;
  rooms: number;
}

export class RoomPickerModel {
  private initAdults: string;
  private initChildren: string;
  private initialRoomCount: number;
  private l10n: ILocalization;
  private isPackageShoppingPath: boolean;

  /* room data structure:
    {
      adults: Number,
      children: [
        Number, Number ... // each number represents AGE
      ]
    }
  */
  @observable
  public rooms: IObservableArray<RoomConfig> = [] as IObservableArray<RoomConfig>;

  constructor({ adults, children }: Travelers = {}, l10n: ILocalization, isPackageShoppingPath: boolean = false) {
    this.l10n = l10n;
    this.isPackageShoppingPath = isPackageShoppingPath;
    this.setupRooms(adults, children);
  }

  private static DEFAULT_CHILD_AGE: number = 0;
  private static MAX_ROOM_COUNT: number = 8;

  get initialProps(): Travelers {
    return {
      adults: this.initAdults,
      children: this.initChildren
    };
  }

  @computed
  get finalProps(): Travelers {
    const adults = this.rooms
      .map(room => room.adults)
      .filter(adult => adult > 0)
      .join(',');

    const children = this.rooms
      .map((room, index) => this.convertRoomChildrenToString(index + 1, room.children))
      .filter(child => child !== '')
      .join(',');

    return { adults, children };
  }

  @computed
  get counts(): TravelerCount {
    return this.rooms.reduce(
      (obj: TravelerCount, room: RoomConfig) => {
        obj.guests += room.adults + room.children.length;

        return obj;
      },
      { guests: 0, rooms: this.rooms.length }
    );
  }

  @computed
  get displaySummary(): string {
    const { rooms, guests } = this.counts;

    let summary: string = '';

    if (rooms === 1) {
      if (guests === 1) {
        summary = this.l10n.formatText('hotels.roomPicker.roomSummary.singleRoomSingleGuest');
      } else {
        summary = this.l10n.formatText('hotels.roomPicker.roomSummary.singleRoomMultipleGuests', guests);
      }
    } else if (guests === 1) {
      summary = this.l10n.formatText('hotels.roomPicker.roomSummary.multipleRoomsSingleGuest', rooms);
    } else {
      summary = this.l10n.formatText('hotels.roomPicker.roomSummary.multipleRoomsMultipleGuests', rooms, guests);
    }

    return summary;
  }

  @computed
  get roomCount(): number {
    return this.rooms.length;
  }

  @computed
  get disableAddRoom(): boolean {
    if (this.isPackageShoppingPath && this.counts.guests >= 6) {
      return true;
    }

    if (this.rooms.length < this.initialRoomCount) {
      return false;
    }

    return this.rooms.length >= RoomPickerModel.MAX_ROOM_COUNT;
  }

  @action
  public addRoom(): void {
    this.rooms.push({ adults: 1, children: [] });
  }

  @action
  public removeRoom(room: RoomConfig): void {
    this.rooms = this.rooms.filter(rm => rm !== room) as IObservableArray<RoomConfig>;
  }

  @action
  public changeAdultsForRoom(room, count): void {
    room.adults = count;
  }

  @action
  public changeChildrenForRoom(room, count): void {
    let diff: number = count - room.children.length;

    if (diff > 0) {
      for (diff; diff > 0; diff--) {
        room.children.push(RoomPickerModel.DEFAULT_CHILD_AGE);
      }
    } else if (diff < 0) {
      room.children.splice(room.children.length + diff, -diff);
    }
  }

  @action
  public changeChildAge(roomIndex: number, childIndex: number, age: number): void {
    this.rooms = observable(
      this.rooms.map((observableRoom, roomI) => {
        if (childIndex > -1 && childIndex < observableRoom.children.length && roomI === roomIndex) {
          observableRoom.children[childIndex] = age;
        }

        return observableRoom;
      })
    );
  }

  @action
  public reset(): void {
    this.setupRooms(this.initAdults, this.initChildren);
  }

  public determineMaxGuests(key: string, currentSectionGuests: number, totalGuests: number): number {
    if (this.isPackageShoppingPath) {
      return currentSectionGuests + 6 - totalGuests;
    } else {
      return key === 'adults' ? 14 : 6;
    }
  }

  // private helpers

  public setupRooms(adultsText: string, childrenText: string) {
    const adults: string[] = adultsText && adultsText.length > 0 ? adultsText.split(',') : [];
    const childrenRooms: number[][] = this.convertChildrenTextToRooms(childrenText);
    const numberOfRooms: number = Math.max(adults.length, childrenRooms.length);
    const rooms: IObservableArray<RoomConfig> = [] as IObservableArray<RoomConfig>;

    for (let i = 0; i < numberOfRooms; i++) {
      // note: parseInt() can return NaN
      const roomAdults: number = i < adults.length ? parseInt(adults[i], 10) : 0;
      const roomChildren: number[] = childrenRooms[i] ? childrenRooms[i] : [];

      rooms.push({
        adults: isNaN(roomAdults) ? 0 : roomAdults,
        children: roomChildren
      });
    }

    this.initAdults = adultsText;
    this.initChildren = childrenText;
    this.rooms.replace(rooms);
    this.initialRoomCount = rooms.length;
  }

  private convertRoomChildrenToString(roomNumber: number, ages: number[]): string {
    const result: string = ages.map((age: number) => `${roomNumber}_${age}`).join(',');

    return result;
  }

  private convertChildrenTextToRooms(childrenText: string): number[][] {
    const items: string[] = childrenText && childrenText.length > 0 ? childrenText.split(',') : [];

    return items.reduce((rooms: number[][], item) => {
      const roomAge: string[] = item.split('_');
      if (roomAge.length !== 2) {
        return rooms; // invalid format sent down
      }

      let roomNumber: number = parseInt(roomAge[0], 10);
      const age: number = parseInt(roomAge[1], 10);
      if (isNaN(roomNumber) || isNaN(age)) {
        return rooms; // invalid data type for room_age
      }
      roomNumber -= 1; // turn to 0-indexed for rooms array

      if (rooms[roomNumber]) {
        rooms[roomNumber].push(age);
      } else {
        rooms[roomNumber] = [age];
      }

      return rooms;
    }, []);
  }
}
